import { useEffect, useState, useSyncExternalStore } from "react";
import { useRouterState } from "@tanstack/react-router";
import { getConsent, setConsent, subscribeConsent, type Consent } from "@/lib/analytics/consent";
import { isAdminPath } from "@/lib/analytics/route-utils";
import { isConfigPresent } from "@/lib/analytics/posthog";
import { Button } from "@/components/ui/button";

/**
 * Sticky bottom banner shown when consent is 'unset' and the visitor is on
 * a non-admin route. Only "Accept" and "Decline" change state — there is no
 * dismiss / Escape shortcut.
 */
export function ConsentBanner() {
  // Gate everything on client-side hydration. During SSR / the first paint
  // localStorage is not available, so we must not render the banner at all —
  // otherwise stored 'granted'/'denied' visitors see a one-second flash before
  // the client snapshot replaces the server snapshot.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const consent = useSyncExternalStore(
    subscribeConsent,
    getConsent,
    () => "unset" as Consent,
  );
  const path = useRouterState({ select: (s) => s.location.pathname });

  if (!hydrated) return null;
  if (!isConfigPresent()) return null;
  if (consent !== "unset") return null;
  if (isAdminPath(path)) return null;

  const choose = (next: Consent) => {
    try {
      setConsent(next);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[consent] persist failed", err);
      }
    }
  };

  return (
    <div
      role="region"
      aria-label="Analytics consent"
      className="fixed inset-x-3 bottom-3 z-50 rounded-lg border border-border/70 bg-background/95 p-4 shadow-lg backdrop-blur md:inset-x-auto md:right-4 md:left-auto md:bottom-4 md:max-w-md"
    >
      <p className="text-sm text-foreground">
        We use privacy-friendly analytics to understand how visitors use this site.
        No personal information is collected.
      </p>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => choose("denied")}>
          Decline
        </Button>
        <Button size="sm" onClick={() => choose("granted")}>
          Accept
        </Button>
      </div>
    </div>
  );
}